import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { CompanyAgentService } from 'src/app/services/company-agent/company-agent.service';
import { CompanyAgentModalService } from 'src/app/services/company-agent-modal/company-agent-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-edit-company-agent',
  templateUrl: './add-edit-company-agent.component.html',
  styleUrls: ['./add-edit-company-agent.component.scss']
})
export class AddEditCompanyAgentComponent extends DestructorComponent implements OnInit {

  @Output() companyAgentAdded = new EventEmitter<void>();
  @Output() companyAgentUpdated = new EventEmitter<void>();

  companyAgentForm!: FormGroup;
  isLoading = false;
  isEditMode = false;
  selectedCompanyAgent: any = null;

  constructor(
    private fb: FormBuilder,
    private _companyAgentService: CompanyAgentService,
    private _companyAgentModalService: CompanyAgentModalService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.setupModalListener();
  }

  private initializeForm(): void {
    this.companyAgentForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]]
    });
  }

  get name() { return this.companyAgentForm.get('name'); }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_company_agent_add_edit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceCompanyAgent = this._companyAgentModalService.getSelectedCompanyAgent();
        if (serviceCompanyAgent) {
          this.isEditMode = true;
          this.selectedCompanyAgent = serviceCompanyAgent;
          this.populateForm(this.selectedCompanyAgent);
        } else {
          this.isEditMode = false;
          this.selectedCompanyAgent = null;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._companyAgentModalService.clearSelectedCompanyAgent();
        this.resetForm();
      });
    }
  }

  private populateForm(companyAgent: any): void {
    this.companyAgentForm.patchValue({
      name: companyAgent.name || ''
    });
  }

  private resetForm(): void {
    this.companyAgentForm.reset();
    this.isLoading = false;
    this.markFormGroupUntouched(this.companyAgentForm);
  }

  private markFormGroupUntouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsUntouched();
      control?.markAsPristine();
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_company_agent_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  onSubmit(): void {
    if (this.companyAgentForm.valid && !this.isLoading) {
      this.isLoading = true;

      const formData = this.companyAgentForm.value;

      const submitData: any = {};
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          submitData[key] = formData[key];
        }
      });

      const apiCall = this.isEditMode
        ? this._companyAgentService.UpdateData(this.selectedCompanyAgent._id, submitData)
        : this._companyAgentService.AddData(submitData);

      this.subscriptions.push(
        apiCall.subscribe({
          next: (response: any) => {
            this.isLoading = false;

            Swal.fire({
              text: this.isEditMode
                ? "Company agent has been successfully updated!"
                : "Company agent has been successfully created!",
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            }).then((result: any) => {
              if (result.isConfirmed) {
                this.closeModal();

                if (this.isEditMode) {
                  this.companyAgentUpdated.emit();
                } else {
                  this.companyAgentAdded.emit();
                }

                this.resetForm();
              }
            });
          },
          error: (error: any) => {
            this.isLoading = false;

            Swal.fire({
              text: error.error?.message || "An error occurred. Please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        })
      );
    } else {
      this.markFormGroupTouched(this.companyAgentForm);
    }
  }
}
